import { Component, type ErrorInfo, type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "@/src/common/components/ui";
import { logger } from "@/src/common/lib/logger";

interface ErrorBoundaryProps {
	children: ReactNode;
	fallback?: ReactNode;
}

interface ErrorBoundaryState {
	hasError: boolean;
	error: Error | null;
}

export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
	state: ErrorBoundaryState = { hasError: false, error: null };

	static getDerivedStateFromError(error: Error): ErrorBoundaryState {
		return { hasError: true, error };
	}

	componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
		logger.error("Unhandled render error caught by ErrorBoundary", error, {
			componentStack: errorInfo.componentStack ?? undefined,
		});
	}

	private handleRetry = (): void => {
		this.setState({ hasError: false, error: null });
	};

	render(): ReactNode {
		if (this.state.hasError) {
			if (this.props.fallback) return this.props.fallback;
			return <ErrorBoundaryFallback onRetry={this.handleRetry} />;
		}
		return this.props.children;
	}
}

interface ErrorBoundaryFallbackProps {
	onRetry: () => void;
}

function ErrorBoundaryFallback({ onRetry }: ErrorBoundaryFallbackProps) {
	const { t } = useTranslation("common");

	return (
		<div className="flex min-h-screen items-center justify-center bg-background px-6">
			<div className="flex max-w-md flex-col items-center text-center">
				<h1 className="text-2xl font-semibold text-foreground">{t("error_boundary_title")}</h1>
				<p className="mt-2 text-balance text-foreground/60">{t("error_boundary_description")}</p>
				<div className="mt-8 flex gap-3">
					<Button variant="outline" onPress={onRetry}>
						{t("error_boundary_retry")}
					</Button>
					<Button variant="primary" onPress={() => window.location.assign("/")}>
						{t("go_home")}
					</Button>
				</div>
			</div>
		</div>
	);
}
